import * as Sentry from '@sentry/nextjs';
import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Plus } from '@phosphor-icons/react';
import { useReCaptcha } from 'next-recaptcha-v3';
import FieldWrapper from '@/components/common/FieldWrapper';
import ContactFormSchema, { ContactFormSchemaType } from './schema';
import useValidationSchema from '@/lib/hooks/useValidationSchema';
import Heading from '../Heading';
import { Lead_Insert_Input, useInsertLeadMutation } from '@/generated/graphql';
import { onAnalyticsFormEvent } from '@/lib/utils/analytics';

export interface ContactFormProps {
  id: number;
  type: 'realtor' | 'agency';
}

const STACK_SPACING = 6;

const ContactForm = ({ id, type }: ContactFormProps) => {
  const { t } = useTranslation(['frontoffice', 'backoffice']);
  const [expandedForm, setExpandedForm] = useState(false);
  const [insert] = useInsertLeadMutation();
  const [success, setSuccess] = useState(false);
  const { executeRecaptcha, loaded } = useReCaptcha();

  const formError = {
    [FORM_ERROR]: t(
      `contact_form_${type}_submission_error`,
      'Yhteydenottopyyntöä ei voitu tallentaa, yritä uudelleen tai ota suoraan yhteyttä välittäjään/välitysliikkeeseen.',
    ),
  };

  const onSubmit = async (values: ContactFormSchemaType) => {
    const analyticsFormName =
      type === 'realtor'
        ? 'Yhteydenotto välittäjälle'
        : 'Yhteydenotto välitysliikkeelle';
    try {
      let token = '';
      if (loaded) {
        token = await executeRecaptcha(`contact_form_submit_${type}`);
      }
      const object: Lead_Insert_Input = {
        ...values,
      };

      if (type === 'realtor') {
        object.user_id = id;
      } else {
        object.real_estate_agency_id = id;
      }

      const { data, errors } = await insert({
        variables: {
          object,
        },
        context: {
          headers: {
            'x-captcha-token': token,
          },
        },
      });

      const affectedRows = data?.insert_lead?.affected_rows || 0;

      if (errors) {
        throw errors;
      }

      if (affectedRows < 1) {
        return formError;
      }
      onAnalyticsFormEvent('submission_success', { name: analyticsFormName });
      return setSuccess(true);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      onAnalyticsFormEvent('submission_failed', { name: analyticsFormName });

      return formError;
    }
  };

  const validate = useValidationSchema(ContactFormSchema);

  if (success) {
    return (
      <Stack spacing={STACK_SPACING} textAlign="center">
        <Heading as="h3" variant="subtitle">
          {t('contact_form_thank_you_title', 'Kiitos yhteydenotostanne!')}
        </Heading>
        <Text>
          {t(
            'contact_form_thank_you_text',
            'Olemme sinuun yhteydessä mahdollisimman pian!',
          )}
        </Text>
      </Stack>
    );
  }

  return (
    <Form<ContactFormSchemaType> onSubmit={onSubmit} validate={validate}>
      {({ handleSubmit, hasValidationErrors, submitError }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={STACK_SPACING}>
            <FieldWrapper
              name="subject"
              label={t('subject', 'Aihe')}
              isRequired
            >
              {({ input }) => <Input {...input} />}
            </FieldWrapper>
            <HStack>
              <FieldWrapper name="name" label={t('name', 'Nimi')} isRequired>
                {({ input }) => <Input {...input} />}
              </FieldWrapper>
              <FieldWrapper
                name="phone"
                label={t('phone_number', 'Puhelin')}
                isRequired
              >
                {({ input }) => <Input {...input} />}
              </FieldWrapper>
            </HStack>
            <HStack>
              <FieldWrapper
                name="email"
                label={t('email', 'Sähköposti')}
                isRequired
              >
                {({ input }) => <Input {...input} />}
              </FieldWrapper>
              <FieldWrapper
                name="city"
                label={t('locality', 'Paikkakunta')}
                isRequired
              >
                {({ input }) => <Input {...input} />}
              </FieldWrapper>
            </HStack>

            {expandedForm ? (
              <FieldWrapper name="message" label={t('message', 'Viesti')}>
                {({ input }) => <Textarea {...input} />}
              </FieldWrapper>
            ) : null}
          </Stack>

          {!expandedForm ? (
            <Button
              colorScheme="primary"
              variant="link"
              size="sm"
              iconSpacing="2"
              leftIcon={<Plus />}
              onClick={() => {
                setExpandedForm(!expandedForm);
              }}
              mt={8}
            >
              {t('add_message', 'Lisää viesti')}
            </Button>
          ) : null}

          <HStack py={6} spacing={10}>
            <Button
              colorScheme="primary"
              type="submit"
              disabled={hasValidationErrors}
            >
              {t('send', 'Lähetä')}
            </Button>

            {submitError ? (
              <Alert status="error">
                <AlertIcon />
                {submitError}
              </Alert>
            ) : null}
          </HStack>
        </form>
      )}
    </Form>
  );
};

export const RealtorContactForm = ({ realtorId }: { realtorId: number }) => (
  <ContactForm id={realtorId} type="realtor" />
);
export const AgencyContactForm = ({ agencyId }: { agencyId: number }) => (
  <ContactForm id={agencyId} type="agency" />
);
